import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function InvoiceSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Audit
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);

  const fetchSettings = () => {
    if (!companyId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/invoicing/settings`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setSettings(d))
      .catch(() => setSettings(null))
      .finally(() => setLoading(false));
  };

  const fetchAudit = () => {
    if (!companyId) return;
    setAuditLoading(true);
    fetch(`${API}/companies/${companyId}/invoicing/audit?page=${auditPage}&per_page=20`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setAudit(Array.isArray(d.data) ? d.data : []);
        setAuditTotalPages(d.last_page || 1);
      })
      .catch(() => setAudit([]))
      .finally(() => setAuditLoading(false));
  };

  useEffect(() => { fetchSettings(); }, [companyId]);
  useEffect(() => { if (activeTab === 'audit') fetchAudit(); }, [activeTab, auditPage]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/invoicing/settings`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setSettings(d);
      alert('Settings saved successfully');
    } catch { alert('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;
  if (!settings) return <div style={pageStyle}><p>Failed to load settings.</p></div>;

  const tabs = [
    { key: 'general', label: '⚙️ General' },
    { key: 'numbering', label: '#️⃣ Numbering' },
    { key: 'footer', label: '📄 Footer & Template' },
    { key: 'rules', label: '📏 Rules' },
    { key: 'audit', label: '📜 Audit History' },
  ];

  return (
    <div style={pageStyle}>
      <h2 style={{ margin: '0 0 20px', fontSize: '1.5rem', fontWeight: 700 }}>⚙️ Invoice Settings</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ padding: '8px 16px', background: activeTab === t.key ? '#fff' : 'transparent', border: 'none', borderBottom: activeTab === t.key ? '2px solid #3b82f6' : '2px solid transparent', color: activeTab === t.key ? '#3b82f6' : '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', marginBottom: '-2px' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Default Currency</label>
              <select value={settings.default_currency} onChange={e => setSettings({ ...settings, default_currency: e.target.value })} style={inputStyle}>
                <option value="USD">USD</option>
                <option value="LBP">LBP</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Default Due Terms</label>
              <select value={settings.default_due_terms} onChange={e => setSettings({ ...settings, default_due_terms: e.target.value })} style={inputStyle}>
                <option value="due_on_receipt">Due on Receipt</option>
                <option value="7_days">7 Days</option>
                <option value="15_days">15 Days</option>
                <option value="30_days">30 Days</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {settings.default_due_terms === 'custom' && (
              <div>
                <label style={labelStyle}>Custom Due Days</label>
                <input type="number" value={settings.custom_due_days || ''} onChange={e => setSettings({ ...settings, custom_due_days: parseInt(e.target.value) || null })} style={inputStyle} min="1" />
              </div>
            )}
            <div>
              <label style={labelStyle}>Due Soon Notification (days before)</label>
              <input type="number" value={settings.due_soon_days} onChange={e => setSettings({ ...settings, due_soon_days: parseInt(e.target.value) || 3 })} style={inputStyle} min="1" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={settings.allow_discount} onChange={e => setSettings({ ...settings, allow_discount: e.target.checked })} />
              <label style={{ fontSize: '0.85rem', color: '#334155' }}>Allow Discounts</label>
            </div>
            {settings.allow_discount && (
              <div>
                <label style={labelStyle}>Discount Threshold (max without approval)</label>
                <input type="number" value={settings.discount_threshold || ''} onChange={e => setSettings({ ...settings, discount_threshold: e.target.value })} style={inputStyle} min="0" step="any" />
              </div>
            )}
          </div>
          <div style={{ marginTop: '20px', textAlign: 'right' }}>
            <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        </div>
      )}

      {/* Numbering Tab */}
      {activeTab === 'numbering' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 16px' }}>
            Invoice and Receipt share one continuous sequence per issuer. Only the prefix changes.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Invoice Prefix</label>
              <input value={settings.invoice_prefix} onChange={e => setSettings({ ...settings, invoice_prefix: e.target.value })} style={inputStyle} maxLength={10} />
            </div>
            <div>
              <label style={labelStyle}>Receipt Prefix</label>
              <input value={settings.receipt_prefix} onChange={e => setSettings({ ...settings, receipt_prefix: e.target.value })} style={inputStyle} maxLength={10} />
            </div>
            <div>
              <label style={labelStyle}>Next Sequence Number</label>
              <input type="number" value={settings.next_sequence} onChange={e => setSettings({ ...settings, next_sequence: parseInt(e.target.value) || 1 })} style={inputStyle} min="1" />
            </div>
            <div>
              <label style={labelStyle}>Number Assignment</label>
              <select value={settings.number_assignment} onChange={e => setSettings({ ...settings, number_assignment: e.target.value })} style={inputStyle}>
                <option value="on_issue">Assign on Issue</option>
                <option value="on_draft">Assign on Draft</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '0.8rem', color: '#475569' }}>
            <strong>Preview:</strong> {settings.invoice_prefix}-{String(settings.next_sequence).padStart(4, '0')} (next invoice) →
            {settings.receipt_prefix}-{String(settings.next_sequence + 1).padStart(4, '0')} (next receipt if created after)
          </div>
          <div style={{ marginTop: '20px', textAlign: 'right' }}>
            <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        </div>
      )}

      {/* Footer Tab */}
      {activeTab === 'footer' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <h4 style={{ margin: '0 0 12px', fontWeight: 600 }}>Thank-You Section</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Footer Thank-You Text</label>
              <textarea value={settings.footer_thank_you_text || ''} onChange={e => setSettings({ ...settings, footer_thank_you_text: e.target.value })} style={{ ...inputStyle, minHeight: '60px' }} placeholder="Thank You For Your Business" />
            </div>
            <div>
              <label style={labelStyle}>Thank-You Text Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={settings.footer_thank_you_color || '#333333'} onChange={e => setSettings({ ...settings, footer_thank_you_color: e.target.value })} style={{ width: '40px', height: '32px', border: 'none', cursor: 'pointer' }} />
                <input value={settings.footer_thank_you_color || '#333333'} onChange={e => setSettings({ ...settings, footer_thank_you_color: e.target.value })} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Company Name Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={settings.footer_company_name_color || '#333333'} onChange={e => setSettings({ ...settings, footer_company_name_color: e.target.value })} style={{ width: '40px', height: '32px', border: 'none', cursor: 'pointer' }} />
                <input value={settings.footer_company_name_color || '#333333'} onChange={e => setSettings({ ...settings, footer_company_name_color: e.target.value })} style={inputStyle} />
              </div>
            </div>
          </div>

          <h4 style={{ margin: '20px 0 12px', fontWeight: 600 }}>Footer Banner</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Banner Background Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={settings.footer_banner_bg_color || '#1e3a5f'} onChange={e => setSettings({ ...settings, footer_banner_bg_color: e.target.value })} style={{ width: '40px', height: '32px', border: 'none', cursor: 'pointer' }} />
                <input value={settings.footer_banner_bg_color || '#1e3a5f'} onChange={e => setSettings({ ...settings, footer_banner_bg_color: e.target.value })} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Banner Text Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={settings.footer_banner_text_color || '#ffffff'} onChange={e => setSettings({ ...settings, footer_banner_text_color: e.target.value })} style={{ width: '40px', height: '32px', border: 'none', cursor: 'pointer' }} />
                <input value={settings.footer_banner_text_color || '#ffffff'} onChange={e => setSettings({ ...settings, footer_banner_text_color: e.target.value })} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={settings.footer_banner_phone || ''} onChange={e => setSettings({ ...settings, footer_banner_phone: e.target.value })} style={inputStyle} placeholder="+1 234 567 890" />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={settings.footer_banner_email || ''} onChange={e => setSettings({ ...settings, footer_banner_email: e.target.value })} style={inputStyle} placeholder="info@company.com" />
            </div>
            <div>
              <label style={labelStyle}>Website</label>
              <input value={settings.footer_banner_website || ''} onChange={e => setSettings({ ...settings, footer_banner_website: e.target.value })} style={inputStyle} placeholder="www.company.com" />
            </div>
          </div>

          {/* Preview */}
          <h4 style={{ margin: '20px 0 8px', fontWeight: 600 }}>Banner Preview</h4>
          <div style={{ background: settings.footer_banner_bg_color || '#1e3a5f', color: settings.footer_banner_text_color || '#fff', padding: '12px 20px', borderRadius: '8px', fontSize: '0.8rem', display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
            {settings.footer_banner_phone && <span>📞 {settings.footer_banner_phone}</span>}
            {settings.footer_banner_email && <span>✉ {settings.footer_banner_email}</span>}
            {settings.footer_banner_website && <span>🌐 {settings.footer_banner_website}</span>}
            {!settings.footer_banner_phone && !settings.footer_banner_email && !settings.footer_banner_website && <span>Configure banner contact info above</span>}
          </div>

          <div style={{ marginTop: '20px', textAlign: 'right' }}>
            <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        </div>
      )}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Service Creation from Invoice</label>
              <select value={settings.service_creation_rule} onChange={e => setSettings({ ...settings, service_creation_rule: e.target.value })} style={inputStyle}>
                <option value="allowed">Allowed</option>
                <option value="management_only">Management Only</option>
                <option value="not_allowed">Not Allowed</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={settings.show_department_on_pdf} onChange={e => setSettings({ ...settings, show_department_on_pdf: e.target.checked })} />
              <label style={{ fontSize: '0.85rem', color: '#334155' }}>Show Department on PDF</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={settings.require_financial_link} onChange={e => setSettings({ ...settings, require_financial_link: e.target.checked })} />
              <label style={{ fontSize: '0.85rem', color: '#334155' }}>Require Financial Link for Issued Invoices</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={settings.holding_invoicing_enabled} onChange={e => setSettings({ ...settings, holding_invoicing_enabled: e.target.checked })} />
              <label style={{ fontSize: '0.85rem', color: '#334155' }}>Enable Holding Invoicing</label>
            </div>
          </div>
          <div style={{ marginTop: '20px', textAlign: 'right' }}>
            <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        </div>
      )}

      {/* Audit Tab */}
      {activeTab === 'audit' && (
        <div style={{ ...cardStyle, padding: '20px' }}>
          {auditLoading ? <p>Loading...</p> : audit.length === 0 ? <p style={{ color: '#94a3b8' }}>No audit records</p> : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Action</th>
                      <th style={thStyle}>Type</th>
                      <th style={thStyle}>Field</th>
                      <th style={thStyle}>Old Value</th>
                      <th style={thStyle}>New Value</th>
                      <th style={thStyle}>User</th>
                      <th style={thStyle}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map(a => (
                      <tr key={a.id}>
                        <td style={tdStyle}><span style={{ ...badgeStyle, background: '#f1f5f9', color: '#475569' }}>{(a.action || '').replace(/_/g, ' ')}</span></td>
                        <td style={tdStyle}>{a.auditable_type}</td>
                        <td style={tdStyle}>{a.field_name || '—'}</td>
                        <td style={{ ...tdStyle, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.old_value || '—'}</td>
                        <td style={{ ...tdStyle, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.new_value || '—'}</td>
                        <td style={tdStyle}>{a.user?.name || 'System'}</td>
                        <td style={tdStyle}>{new Date(a.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {auditTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
                  <button onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage <= 1} style={paginationBtn}>← Prev</button>
                  <span style={{ padding: '6px 12px', fontSize: '0.85rem', color: '#64748b' }}>Page {auditPage} of {auditTotalPages}</span>
                  <button onClick={() => setAuditPage(p => Math.min(auditTotalPages, p + 1))} disabled={auditPage >= auditTotalPages} style={paginationBtn}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1000px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#334155', fontSize: '0.8rem' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize' };
const paginationBtn = { padding: '6px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' };
